/**
 * Closed, stable navigation contract exposed to AI tools.
 *
 * The deep-link route is intentionally separate from the application's
 * presentation route. `/app/...` links are authenticated entry points that
 * must be reauthorized before they resolve to an existing SPA route.
 */

export const APP_NAVIGATION_DESTINATION_IDS = [
  "home",
  "ai",
  "account",
  "notifications",
  "notificationDetail",
  "requestSupport",
  "league",
  "admin",
  "billing",
  "leagueBilling",
  "aiUsageLimits",
  "supportInbox",
  "espnAccessRequests",
  "leagueSettings",
  "leagueInvitations",
  "athleteSchema",
  "athleteCreation",
  "externalSources",
  "competitionSchema",
  "competitionCreation",
  "organizationAdminSchema",
  "recentActivity",
  "competitions",
  "organizations",
  "athletes",
  "leagueStaff",
  "gpsMenu",
  "competition",
  "athlete",
  "staff",
  "overlay",
] as const;

export type SBotAppDestinationID = (typeof APP_NAVIGATION_DESTINATION_IDS)[number];
export type SBotAppDestinationScope = "global" | "workspace" | "resource" | "entity";

export interface SBotAppRoute {
  destination: SBotAppDestinationID;
  leagueID?: string | undefined;
  resourceID?: string | undefined;
}

export interface SBotAppDestinationDescriptor {
  id: SBotAppDestinationID;
  title: string;
  breadcrumb: string;
  handle: string;
  route: SBotAppRoute;
}

export interface SBotAppNavigationResourceResolution {
  /** Required when resolving an overlay to its current designer route. */
  competitionInstanceID?: string | undefined;
}

interface DestinationDefinition {
  scope: SBotAppDestinationScope;
  title: string;
  breadcrumb: string;
  appPath: string;
}

const DESTINATION_DEFINITIONS: Record<SBotAppDestinationID, DestinationDefinition> = {
  home: { scope: "global", title: "Home", breadcrumb: "Home", appPath: "/home" },
  ai: { scope: "global", title: "AI", breadcrumb: "AI", appPath: "/ai" },
  account: { scope: "global", title: "Account", breadcrumb: "Account", appPath: "/account" },
  notifications: {
    scope: "global",
    title: "Notifications",
    breadcrumb: "Account / Notifications",
    appPath: "/account/notifications",
  },
  notificationDetail: {
    scope: "resource",
    title: "Notification",
    breadcrumb: "Account / Notification",
    appPath: "/account/notifications",
  },
  requestSupport: {
    scope: "global",
    title: "Request Support",
    breadcrumb: "Account / Request Support",
    appPath: "/account/support",
  },
  league: { scope: "workspace", title: "League", breadcrumb: "League", appPath: "/league" },
  admin: { scope: "global", title: "Admin", breadcrumb: "SBot Admin / Admin", appPath: "/admin" },
  billing: { scope: "global", title: "Billing", breadcrumb: "SBot Admin / Billing", appPath: "/admin/billing" },
  leagueBilling: {
    scope: "global",
    title: "League Billing",
    breadcrumb: "SBot Admin / League Billing",
    appPath: "/admin/league-billing",
  },
  aiUsageLimits: {
    scope: "global",
    title: "AI Usage Limits",
    breadcrumb: "SBot Admin / AI Usage Limits",
    appPath: "/admin/ai-usage",
  },
  supportInbox: {
    scope: "global",
    title: "Support Inbox",
    breadcrumb: "SBot Admin / Support Inbox",
    appPath: "/admin/support",
  },
  espnAccessRequests: {
    scope: "global",
    title: "ESPN Access Requests",
    breadcrumb: "SBot Admin / ESPN Access Requests",
    appPath: "/admin/espn-access",
  },
  leagueSettings: {
    scope: "workspace",
    title: "League Settings",
    breadcrumb: "League / League Settings",
    appPath: "/league/settings",
  },
  leagueInvitations: {
    scope: "workspace",
    title: "League Invitations",
    breadcrumb: "League / League Invitations",
    appPath: "/league/invitations",
  },
  athleteSchema: {
    scope: "workspace",
    title: "Athlete Data",
    breadcrumb: "League / Athlete Data",
    appPath: "/league/athletes",
  },
  athleteCreation: {
    scope: "workspace",
    title: "Create Athlete",
    breadcrumb: "League / Create Athlete",
    appPath: "/league/athletes/new",
  },
  externalSources: {
    scope: "workspace",
    title: "External Data Sources",
    breadcrumb: "League / External Data Sources",
    appPath: "/league/external-sources",
  },
  competitionSchema: {
    scope: "workspace",
    title: "Competition Data",
    breadcrumb: "League / Competition Data",
    appPath: "/league/competitions",
  },
  competitionCreation: {
    scope: "workspace",
    title: "Create Competition",
    breadcrumb: "League / Create Competition",
    appPath: "/league/competitions/new",
  },
  organizationAdminSchema: {
    scope: "workspace",
    title: "Organization Admin Data",
    breadcrumb: "League / Organization Admin Data",
    appPath: "/league/org-admin",
  },
  recentActivity: {
    scope: "workspace",
    title: "Recent Activity",
    breadcrumb: "Home / Recent Activity",
    appPath: "/home/activity",
  },
  competitions: {
    scope: "workspace",
    title: "Competitions",
    breadcrumb: "Home / Competitions",
    appPath: "/home/schedule",
  },
  organizations: {
    scope: "workspace",
    title: "Organizations",
    breadcrumb: "Home / Organizations",
    appPath: "/home/organizations",
  },
  athletes: {
    scope: "workspace",
    title: "Athletes",
    breadcrumb: "Home / Athletes",
    appPath: "/home/athletes",
  },
  leagueStaff: {
    scope: "workspace",
    title: "League Staff",
    breadcrumb: "Home / League Staff",
    appPath: "/home/staff",
  },
  gpsMenu: {
    scope: "workspace",
    title: "GPS Tracking",
    breadcrumb: "Home / GPS Tracking",
    appPath: "/home/gps",
  },
  competition: {
    scope: "entity",
    title: "Competition",
    breadcrumb: "Home / Competition",
    appPath: "/competitions",
  },
  athlete: {
    scope: "entity",
    title: "Athlete",
    breadcrumb: "Home / Athletes / Athlete",
    appPath: "/home/athletes",
  },
  staff: {
    scope: "entity",
    title: "Staff Member",
    breadcrumb: "Home / League Staff / Staff Member",
    appPath: "/home/staff",
  },
  overlay: {
    scope: "entity",
    title: "Broadcast Overlay",
    breadcrumb: "Home / Broadcast Overlay",
    appPath: "/competitions",
  },
};

const DESTINATION_IDS = new Set<string>(APP_NAVIGATION_DESTINATION_IDS);

export type SBotAppRouteCodecErrorKind =
  | "invalidURL"
  | "unknownDestination"
  | "invalidParameters";

export class SBotAppRouteCodecError extends Error {
  readonly kind: SBotAppRouteCodecErrorKind;
  readonly destination?: SBotAppDestinationID | undefined;

  constructor(kind: SBotAppRouteCodecErrorKind, destination?: SBotAppDestinationID) {
    const message = kind === "invalidURL"
      ? "This is not a valid SBot app link."
      : kind === "unknownDestination"
        ? "This SBot app link points to an unknown destination."
        : "This SBot app link does not have the required destination parameters.";
    super(message);
    this.name = "SBotAppRouteCodecError";
    this.kind = kind;
    this.destination = destination;
  }
}

export function appDestinationScope(destination: SBotAppDestinationID): SBotAppDestinationScope {
  return DESTINATION_DEFINITIONS[destination].scope;
}

export function isEntityAppDestination(destination: SBotAppDestinationID): boolean {
  return appDestinationScope(destination) === "entity";
}

export function isWorkspaceScopedAppDestination(destination: SBotAppDestinationID): boolean {
  const scope = appDestinationScope(destination);
  return scope === "workspace" || scope === "entity";
}

export function isResourceAppDestination(destination: SBotAppDestinationID): boolean {
  return appDestinationScope(destination) === "resource";
}

export function appDestinationDescriptor(route: SBotAppRoute): SBotAppDestinationDescriptor {
  const validated = validateAppRoute(route);
  const definition = DESTINATION_DEFINITIONS[validated.destination];
  return {
    id: validated.destination,
    title: definition.title,
    breadcrumb: definition.breadcrumb,
    handle: makeAppViewHandle(validated.destination),
    route: validated,
  };
}

export function makeAppViewHandle(destination: SBotAppDestinationID): string {
  return `appView:${destination}`;
}

export function makeAppEntityHandle(
  destination: Extract<SBotAppDestinationID, "competition" | "athlete" | "staff" | "overlay">,
  resourceID: string,
): string {
  const normalizedResourceID = trimmedParameter(resourceID);
  if (!normalizedResourceID) throw new SBotAppRouteCodecError("invalidParameters", destination);
  return `${destination}:${normalizedResourceID}`;
}

export function validateAppRoute(route: SBotAppRoute): SBotAppRoute {
  const definition = DESTINATION_DEFINITIONS[route.destination];
  const leagueID = trimmedParameter(route.leagueID);
  const resourceID = trimmedParameter(route.resourceID);

  if (definition.scope === "entity") {
    if (!leagueID || !resourceID) throw new SBotAppRouteCodecError("invalidParameters", route.destination);
    return { destination: route.destination, leagueID, resourceID };
  }

  if (definition.scope === "workspace") {
    if (!leagueID || resourceID !== undefined) {
      throw new SBotAppRouteCodecError("invalidParameters", route.destination);
    }
    return { destination: route.destination, leagueID };
  }

  if (definition.scope === "resource") {
    if (leagueID !== undefined || !resourceID) {
      throw new SBotAppRouteCodecError("invalidParameters", route.destination);
    }
    return { destination: route.destination, resourceID };
  }

  if (leagueID !== undefined || resourceID !== undefined) {
    throw new SBotAppRouteCodecError("invalidParameters", route.destination);
  }
  return { destination: route.destination };
}

export function appDeepLinkPath(route: SBotAppRoute): string {
  const validated = validateAppRoute(route);
  const segments = [
    validated.destination,
    ...(validated.leagueID ? [validated.leagueID] : []),
    ...(validated.resourceID ? [validated.resourceID] : []),
  ];
  return `/app/${segments.map(encodePathSegment).join("/")}`;
}

export function appDeepLinkURL(route: SBotAppRoute, origin: string): string {
  const normalizedOrigin = normalizedHTTPOrigin(origin);
  return `${normalizedOrigin}${appDeepLinkPath(route)}`;
}

export function isAppDeepLinkPath(pathname: string): boolean {
  return pathname === "/app" || pathname.startsWith("/app/");
}

export function parseAppDeepLink(
  input: string | URL,
  options: { expectedOrigin?: string | undefined } = {},
): SBotAppRoute {
  let url: URL;
  try {
    url = input instanceof URL ? input : new URL(input, options.expectedOrigin ?? "https://sbot.invalid");
  } catch {
    throw new SBotAppRouteCodecError("invalidURL");
  }

  if (
    url.username
    || url.password
    || url.search
    || url.hash
    || (url.protocol !== "http:" && url.protocol !== "https:")
  ) {
    throw new SBotAppRouteCodecError("invalidURL");
  }

  if (options.expectedOrigin && url.origin !== normalizedHTTPOrigin(options.expectedOrigin)) {
    throw new SBotAppRouteCodecError("invalidURL");
  }

  const rawSegments = url.pathname.split("/");
  if (
    rawSegments.length < 3
    || rawSegments[0] !== ""
    || rawSegments[1] !== "app"
    || rawSegments.some((segment, index) => index >= 2 && segment.length === 0)
  ) {
    throw new SBotAppRouteCodecError("invalidURL");
  }

  const segments = rawSegments.slice(2).map(decodePathSegment);
  if (segments.length < 1 || segments.length > 3) {
    throw new SBotAppRouteCodecError("invalidParameters");
  }

  const destinationValue = segments[0] ?? "";
  if (!DESTINATION_IDS.has(destinationValue)) {
    throw new SBotAppRouteCodecError("unknownDestination");
  }
  const destination = destinationValue as SBotAppDestinationID;
  if (appDestinationScope(destination) === "resource") {
    return validateAppRoute({
      destination,
      resourceID: segments[1],
      // Supplying a third segment must remain invalid rather than being
      // silently discarded by the resource-only route shape.
      leagueID: segments[2],
    });
  }
  return validateAppRoute({
    destination,
    leagueID: segments[1],
    resourceID: segments[2],
  });
}

export function appRouteToSPAPath(
  route: SBotAppRoute,
  resource: SBotAppNavigationResourceResolution = {},
): string {
  const validated = validateAppRoute(route);
  if (validated.destination === "competition") {
    return `/competitions/${encodePathSegment(validated.resourceID!)}`;
  }
  if (validated.destination === "athlete") {
    return `/home/athletes/${encodePathSegment(validated.resourceID!)}`;
  }
  if (validated.destination === "staff") {
    return `/home/staff/${encodePathSegment(validated.resourceID!)}`;
  }
  if (validated.destination === "overlay") {
    const competitionInstanceID = trimmedParameter(resource.competitionInstanceID);
    if (!competitionInstanceID) {
      throw new SBotAppRouteCodecError("invalidParameters", validated.destination);
    }
    return `/competitions/${encodePathSegment(competitionInstanceID)}/overlays/${encodePathSegment(validated.resourceID!)}`;
  }
  if (validated.destination === "notificationDetail") {
    return `/account/notifications/${encodePathSegment(validated.resourceID!)}`;
  }
  return DESTINATION_DEFINITIONS[validated.destination].appPath;
}

function trimmedParameter(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function encodePathSegment(value: string): string {
  if (!value) throw new SBotAppRouteCodecError("invalidURL");
  return encodeURIComponent(value).replace(/[!'()*]/g, (character) =>
    `%${character.charCodeAt(0).toString(16).toUpperCase()}`,
  );
}

function decodePathSegment(value: string): string {
  try {
    if (!value) throw new SBotAppRouteCodecError("invalidURL");
    const decoded = decodeURIComponent(value);
    if (!decoded) throw new SBotAppRouteCodecError("invalidURL");
    return decoded;
  } catch (error) {
    if (error instanceof SBotAppRouteCodecError) throw error;
    throw new SBotAppRouteCodecError("invalidURL");
  }
}

function normalizedHTTPOrigin(origin: string): string {
  try {
    const url = new URL(origin);
    if (
      (url.protocol !== "http:" && url.protocol !== "https:")
      || url.username
      || url.password
      || url.pathname !== "/"
      || url.search
      || url.hash
    ) {
      throw new SBotAppRouteCodecError("invalidURL");
    }
    return url.origin;
  } catch (error) {
    if (error instanceof SBotAppRouteCodecError) throw error;
    throw new SBotAppRouteCodecError("invalidURL");
  }
}
